import { Dispatch } from 'redux';
import { createAction } from '@reduxjs/toolkit';

import apiRequestService from 'common/services/api/apiRequestService';
import { Credentials, Method } from 'common/services/http/types';
import bulkUserSettingsService from 'common/services/userSettings/bulkUserSettingsService';
import { detectLanguage } from 'common/i18n/loadLanguage';
import { UserSettings } from 'common/services/userSettings/types';
import { ALLOWED_LANGUAGES } from 'common/i18n/constants';
import { getQueryParamsFromUrl } from 'common/helpers/Url';
import setUserSettingsService from 'common/services/userSettings/setUserSettingsService';
import { loginSuccessAction, resetUserAuthAction } from 'users/reducers/auth';
import { AffiliatesConfigState, ControlConfigState } from 'common/types';

import checkWebpSupport from '../helpers/checkWebpSupport';
import { changeWebpSupportAction } from '../reducers/ui';
import httpRequestService from '../services/http/httpRequestService';

import isMain from './utils/isMain';

export const APP_INIT = 'config/APP_INIT';

export const configFetchAction = createAction('fetch config');
export const configUpdateAction =
  createAction<Partial<ControlConfigState | AffiliatesConfigState>>(
    'update config',
  );
export const configFetchSuccessAction = createAction<
  ControlConfigState | AffiliatesConfigState
>('fetch config success');

const setLanguageTo = (dispatch, language) => {
  dispatch(configUpdateAction({ language: detectLanguage(language) }));
};

export const fetchConfig = () => (dispatch: Dispatch) => {
  dispatch(configFetchAction());

  return checkWebpSupport()
    .then(() => {
      dispatch(changeWebpSupportAction(true));
    })
    .finally(() =>
      apiRequestService({
        method: '/config',
        httpMethod: Method.Get,
      }).then((config) => {
        dispatch(configFetchSuccessAction(config));

        if (config.user) {
          const { language } = getQueryParamsFromUrl(document.location.href);

          return (
            language && ALLOWED_LANGUAGES.includes(language as string)
              ? setUserSettingsService({
                  name: UserSettings.Languages,
                  value: language as string,
                }).then(() => language)
              : bulkUserSettingsService.get([
                  {
                    name: UserSettings.Languages,
                    format: 'text',
                  },
                ])
          )
            .then((language) => {
              setLanguageTo(dispatch, language);
            })
            .catch(() => setLanguageTo(dispatch, null))
            .finally(() =>
              dispatch(
                loginSuccessAction({
                  user: config.user,
                }),
              ),
            );
        }

        if (APP_NAME === 'affiliates' && isMain(window.location)) {
          const [_, host] = config.url.userImpressionUrl.split('//');

          httpRequestService({
            method: Method.Get,
            host,
            pathname: window.location.search,
            credentials: Credentials.Omit,
          });
        }

        setLanguageTo(dispatch, null);

        dispatch(resetUserAuthAction());

        return Promise.resolve();
      }),
    );
};
